import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API, useLanguage } from "../App";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import {
  BarChart3, RefreshCw, TrendingUp, Target, Users, Activity,
  Loader2, ArrowRight, Calendar, MousePointerClick, PhoneCall,
  LogOut, Sparkles, AlertCircle
} from "lucide-react";
import { toast } from "sonner";

const STEP_LABELS = [
  "0 · Cover",
  "1 · Problema",
  "2 · Agenda / Risorse",
  "3 · Prenotazione online",
  "4 · WhatsApp",
  "5 · Reminder",
  "6 · Recall",
  "7 · Pacchetti / Preventivi",
  "8 · Analytics",
  "9 · Closing",
];

const VARIANTS = [
  { value: "odonto",   label: "Odonto · /demo-odonto" },
  { value: "fisio",    label: "Fisio/Osteo · /demo-benessere-posturale" },
  { value: "estetica", label: "Estetica · /demo-estetica" },
  { value: "hair",     label: "Hair · /demo-hair" },
  { value: "generic",  label: "Generica · /demo /demo-live" },
  { value: "__all__",  label: "Tutte le demo" },
];

const DemoFunnelDashboard = () => {
  const { language } = useLanguage();
  const [variant, setVariant] = useState("odonto");
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async (v = variant, d = days) => {
    setLoading(true);
    try {
      const params = { days: d };
      if (v !== "__all__") params.variant = v;
      const res = await axios.get(`${API}/demo/events/funnel`, { params, withCredentials: true });
      setData(res.data);
    } catch (err) {
      toast.error(language === "it" ? "Errore nel caricamento" : "Loading error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  // Normalize funnel for rendering
  const funnelMap = useMemo(() => {
    const m = {};
    (data?.funnel || []).forEach(f => { m[f.event] = f; });
    return m;
  }, [data]);

  const stepCompletions = useMemo(() => {
    // Build array of [step, count] from step_completed.by_step
    const map = funnelMap.step_completed?.by_step || {};
    return Array.from({ length: 10 }, (_, i) => {
      const unique = map[String(i)] || 0;
      return { step: i, unique };
    });
  }, [funnelMap]);

  // Drop-off calculation (cover → step N)
  const coverUnique = funnelMap.demo_started?.unique || 0;
  const lastStepUnique = stepCompletions[8]?.unique || 0;  // step 8 = last step before closing
  const clickedTrial = data?.clicked_trial || 0;
  const clickedCall = data?.clicked_call || 0;

  return (
    <div className="space-y-6" data-testid="demo-funnel-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <BarChart3 className="w-6 h-6 text-cyan-500" />
            {language === "it" ? "Demo Funnel" : "Demo Funnel"}
          </h2>
          <p className="text-sm text-muted-foreground mt-0.5">
            {language === "it" ? "Analisi conversione demo interattive" : "Interactive demo conversion analysis"}
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <Select value={variant} onValueChange={(v) => { setVariant(v); load(v, days); }}>
            <SelectTrigger className="w-[190px] rounded-full h-9" data-testid="funnel-variant-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {VARIANTS.map(v => <SelectItem key={v.value} value={v.value}>{v.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={String(days)} onValueChange={(v) => { const n = parseInt(v, 10); setDays(n); load(variant, n); }}>
            <SelectTrigger className="w-[120px] rounded-full h-9" data-testid="funnel-days-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">{language === "it" ? "Ultimi 7gg" : "Last 7d"}</SelectItem>
              <SelectItem value="30">{language === "it" ? "Ultimi 30gg" : "Last 30d"}</SelectItem>
              <SelectItem value="90">{language === "it" ? "Ultimi 90gg" : "Last 90d"}</SelectItem>
            </SelectContent>
          </Select>
          <Button
            variant="outline" size="sm" onClick={() => load()}
            className="rounded-full" data-testid="funnel-refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? "animate-spin" : ""}`} />
            {language === "it" ? "Aggiorna" : "Refresh"}
          </Button>
        </div>
      </div>

      {loading && !data ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div>
      ) : !data || data.unique_sessions === 0 ? (
        <div className="text-center py-16 rounded-2xl border border-dashed border-border bg-muted/30" data-testid="funnel-empty">
          <AlertCircle className="w-12 h-12 mx-auto text-muted-foreground opacity-40 mb-3" />
          <p className="font-medium text-sm text-foreground">
            {language === "it" ? "Nessun evento tracciato" : "No events tracked"}
          </p>
          <p className="text-xs text-muted-foreground mt-1 max-w-sm mx-auto">
            {language === "it"
              ? `Nessuna sessione demo per "${VARIANTS.find(x => x.value === variant)?.label || variant}" negli ultimi ${days} giorni. Le demo verticali (/demo-odonto, /demo-benessere-posturale, /demo-estetica, /demo-hair) tracciano automaticamente gli eventi.`
              : "No demo sessions in the selected period."}
          </p>
        </div>
      ) : (
        <>
          {/* KPI Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="funnel-kpi-cards">
            <KpiCard
              icon={Users}
              label={language === "it" ? "Sessioni uniche" : "Unique sessions"}
              value={data.unique_sessions || 0}
              color="from-blue-500 to-cyan-600"
              testId="kpi-sessions"
            />
            <KpiCard
              icon={Activity}
              label={language === "it" ? "Demo avviate" : "Demo started"}
              value={data.demo_started || 0}
              color="from-violet-500 to-fuchsia-600"
              testId="kpi-started"
            />
            <KpiCard
              icon={Target}
              label={language === "it" ? "Click trial" : "Clicked trial"}
              value={data.clicked_trial || 0}
              color="from-emerald-500 to-teal-600"
              testId="kpi-trial"
            />
            <KpiCard
              icon={PhoneCall}
              label={language === "it" ? "Click consulente" : "Clicked call"}
              value={data.clicked_call || 0}
              color="from-amber-500 to-orange-600"
              testId="kpi-call"
            />
            <KpiCard
              icon={TrendingUp}
              label={language === "it" ? "Conversion rate" : "Conversion"}
              value={`${data.trial_conversion_rate || 0}%`}
              color="from-pink-500 to-rose-600"
              testId="kpi-rate"
            />
          </div>

          {/* Funnel visualizer */}
          <div className="bg-card border border-border rounded-2xl p-5" data-testid="funnel-steps">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                <BarChart3 className="w-4 h-4 text-cyan-500" />
                {language === "it" ? "Completamento per step" : "Completion by step"}
              </h3>
              <span className="text-xs text-muted-foreground">
                {coverUnique} {language === "it" ? "avvii" : "starts"} → {clickedTrial} {language === "it" ? "trial" : "trials"}
              </span>
            </div>
            <div className="space-y-2">
              {stepCompletions.map((s) => {
                const pct = coverUnique ? Math.round((s.unique / coverUnique) * 100) : 0;
                const barColor = s.step < 3 ? "from-blue-500 to-cyan-500"
                  : s.step < 6 ? "from-cyan-500 to-emerald-500"
                  : s.step < 9 ? "from-emerald-500 to-teal-500"
                  : "from-amber-500 to-orange-500";
                return (
                  <div key={s.step} className="flex items-center gap-3 text-xs" data-testid={`funnel-step-${s.step}`}>
                    <span className="w-36 text-muted-foreground truncate">{STEP_LABELS[s.step]}</span>
                    <div className="flex-1 h-6 rounded-full bg-muted overflow-hidden relative">
                      <div className={`h-full bg-gradient-to-r ${barColor} transition-all duration-500 flex items-center px-2 text-white font-semibold`}
                        style={{ width: `${pct}%`, minWidth: pct > 0 ? "32px" : "0" }}>
                        {pct > 8 ? `${pct}%` : ""}
                      </div>
                    </div>
                    <span className="w-20 text-right font-medium text-foreground">{s.unique}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Drop-off analysis */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-card border border-border rounded-2xl p-5" data-testid="funnel-dropoff">
              <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                <LogOut className="w-4 h-4 text-red-500" />
                {language === "it" ? "Drop-off principale" : "Main drop-off"}
              </h3>
              {(() => {
                // Find biggest drop between consecutive steps
                let biggest = { from: 0, to: 1, drop: 0, dropPct: 0 };
                for (let i = 0; i < stepCompletions.length - 1; i++) {
                  const cur = stepCompletions[i].unique;
                  const nxt = stepCompletions[i + 1].unique;
                  const drop = cur - nxt;
                  const dropPct = cur ? (drop / cur) * 100 : 0;
                  if (drop > biggest.drop) biggest = { from: i, to: i + 1, drop, dropPct };
                }
                if (biggest.drop === 0) {
                  return <p className="text-sm text-muted-foreground">{language === "it" ? "Nessun drop-off significativo" : "No significant drop-off"}</p>;
                }
                return (
                  <div>
                    <p className="text-2xl font-bold text-red-500">-{Math.round(biggest.dropPct)}%</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {STEP_LABELS[biggest.from]} → {STEP_LABELS[biggest.to]}
                    </p>
                    <p className="text-xs text-foreground mt-2">
                      {biggest.from === 0 ? (
                        language === "it"
                          ? `${biggest.drop} utenti lasciano la cover prima di iniziare. Migliora l'hook iniziale (headline, KPI visibili, CTA).`
                          : `${biggest.drop} users leave before starting. Improve the cover hook (headline, visible KPI, CTA).`
                      ) : (
                        language === "it"
                          ? `${biggest.drop} utenti persi tra questi due step. Ottimizza lo step ${biggest.from}: riduci il testo o rendi la transizione più chiara.`
                          : `${biggest.drop} users lost here. Optimize step ${biggest.from}: shorten the text or clarify the transition.`
                      )}
                    </p>
                  </div>
                );
              })()}
            </div>

            <div className="bg-card border border-border rounded-2xl p-5" data-testid="funnel-conversion-detail">
              <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                <MousePointerClick className="w-4 h-4 text-emerald-500" />
                {language === "it" ? "CTA cliccate" : "CTA clicks"}
              </h3>
              <div className="space-y-3">
                <CtaRow
                  label={language === "it" ? "Attiva prova gratuita" : "Start free trial"}
                  count={clickedTrial}
                  total={coverUnique}
                  color="text-emerald-500"
                />
                <CtaRow
                  label={language === "it" ? "Prenota demo consulente" : "Book consultant demo"}
                  count={clickedCall}
                  total={coverUnique}
                  color="text-amber-500"
                />
              </div>
              <div className="mt-4 p-3 rounded-lg bg-gradient-to-br from-cyan-500/10 to-blue-500/10 border border-cyan-500/20 text-xs text-foreground">
                <div className="flex items-center gap-1.5 font-semibold text-cyan-600 dark:text-cyan-300 mb-1">
                  <Sparkles className="w-3 h-3" /> Suggestion
                </div>
                {coverUnique > 0 && clickedTrial / coverUnique < 0.1 ? (
                  <p>{language === "it"
                    ? "Tasso di conversione trial sotto il 10%. Considera di rafforzare la CTA finale o aggiungere urgenza (es. limited time)."
                    : "Trial conversion under 10%. Consider strengthening the final CTA or adding urgency."}</p>
                ) : lastStepUnique < coverUnique * 0.3 ? (
                  <p>{language === "it"
                    ? "Meno del 30% raggiunge l'ultimo step. Prova ad accorciare la demo o migliorare le transizioni."
                    : "Less than 30% reach the last step. Consider shortening the demo."}</p>
                ) : (
                  <p>{language === "it"
                    ? "Il funnel sta performando bene! Continua a monitorare le metriche per ottimizzare ulteriormente."
                    : "Your funnel is performing well. Keep monitoring to further optimize."}</p>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const KpiCard = ({ icon: Icon, label, value, color, testId }) => (
  <div className={`p-4 rounded-2xl bg-gradient-to-br ${color} text-white shadow-lg`} data-testid={testId}>
    <Icon className="w-5 h-5 opacity-80 mb-1.5" />
    <p className="text-[10px] uppercase tracking-wider opacity-90">{label}</p>
    <p className="text-2xl font-bold mt-0.5">{value}</p>
  </div>
);

const CtaRow = ({ label, count, total, color }) => {
  const pct = total ? Math.round((count / total) * 100) : 0;
  return (
    <div>
      <div className="flex items-center justify-between text-xs mb-1">
        <span className="text-foreground font-medium">{label}</span>
        <span className={`${color} font-bold`}>{count} · {pct}%</span>
      </div>
      <div className="h-1.5 rounded-full bg-muted overflow-hidden">
        <div className={`h-full ${color.replace("text-", "bg-")} transition-all`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

export default DemoFunnelDashboard;
